// File: src/components/account/OrderDetails.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";
import logger from "../../utils/logger.js";
import "../../styles/order-details.css";

export default function OrderDetails() {
  const { id } = useParams();
  const { lang, dir } = useLanguage();
  const isAr = lang === "ar";

  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await api.apiGet(`orders/${id}`);

        const ok =
          data &&
          (
            data.success === true ||
            data.status === "success" ||
            !!data.order
          );

        if (!ok || !data.order) {
          setError(isAr ? "خطأ في تحميل الطلب" : "Error loading order");
          return;
        }

        const orderData = data.order || {};
        const orderItems = Array.isArray(orderData.items) ? orderData.items : [];

        setOrder(orderData);
        setItems(orderItems);
        setInvoice(data.invoice || orderData.invoice || null);
      } catch (e) {
        logger.error(e);
        setError(isAr ? "حدث خطأ أثناء تحميل الطلب" : "Error loading order");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, isAr]);

  const labels = useMemo(
    () => ({
      loading: isAr ? "جارٍ التحميل..." : "Loading...",
      notFound: isAr ? "الطلب غير موجود" : "Order not found",
      paid: isAr ? "مدفوع" : "Paid",
      unpaid: isAr ? "غير مدفوع" : "Unpaid",
      paymentMethod: isAr ? "طريقة الدفع" : "Payment Method",
      orderStatus: isAr ? "حالة الطلب" : "Order Status",
      shipping: isAr ? "الشحن" : "Shipping",
      total: isAr ? "الإجمالي" : "Total",
      products: isAr ? "المنتجات" : "Products",
      noItems: isAr ? "لا توجد عناصر" : "No items",
      qty: isAr ? "الكمية:" : "Qty:",
      viewInvoice: isAr ? "عرض الفاتورة" : "View Invoice",
      downloads: isAr ? "التحميلات" : "My Downloads",
      backOrders: isAr ? "رجوع للطلبات" : "Back to Orders",
      trackingCode: isAr ? "رقم التتبع" : "Tracking Code",
      carrier: isAr ? "شركة الشحن" : "Carrier",
      shippedAt: isAr ? "تم الشحن في" : "Shipped At",
      deliveredAt: isAr ? "تم التسليم في" : "Delivered At",
      shippingAddress: isAr ? "عنوان الشحن" : "Shipping Address",
      paymentStatus: isAr ? "حالة الدفع" : "Payment Status",
      shippingPrice: isAr ? "سعر الشحن" : "Shipping Price",
      orderDate: isAr ? "تاريخ الطلب" : "Order Date",
      shippingMethod: isAr ? "طريقة الشحن" : "Shipping Method",
      subtotal: isAr ? "المجموع الفرعي" : "Subtotal",
      coinsUsed: isAr ? "النقاط المستخدمة" : "Coins Used",
      coinsDiscount: isAr ? "خصم النقاط" : "Coins Discount",
      createdAt: isAr ? "تاريخ الإنشاء" : "Created At",
      details: isAr ? "تفاصيل الطلب" : "Order Details",
      summary: isAr ? "ملخص الطلب" : "Order Summary",
      trackingDetails: isAr ? "معلومات التتبع" : "Tracking Details",
      paymentDetails: isAr ? "معلومات الدفع" : "Payment Details",
      na: "N/A",
      notAvailable: isAr ? "غير متوفر" : "Not available",
      digitalOrder: isAr ? "طلب رقمي" : "Digital Order",
      noShippingRequired: isAr ? "لا يحتاج شحن" : "No shipping required",
      paymentReference: isAr ? "مرجع الدفع" : "Payment Reference",
      invoiceNumber: isAr ? "رقم الفاتورة" : "Invoice Number",
    }),
    [isAr]
  );

  const formatStatus = (value) => {
    const s = String(value || "").trim().toLowerCase();
    if (!s) return labels.na;

    const map = {
      pending: isAr ? "قيد الانتظار" : "Pending",
      processing: isAr ? "قيد المعالجة" : "Processing",
      completed: isAr ? "مكتمل" : "Completed",
      canceled: isAr ? "ملغي" : "Canceled",
      cancelled: isAr ? "ملغي" : "Cancelled",
      paid: isAr ? "مدفوع" : "Paid",
      unpaid: isAr ? "غير مدفوع" : "Unpaid",
      failed: isAr ? "فشل" : "Failed",
      refunded: isAr ? "مسترجع" : "Refunded",
      not_required: isAr ? "غير مطلوب" : "Not required",
      shipped: isAr ? "تم الشحن" : "Shipped",
      in_transit: isAr ? "في الطريق" : "In transit",
      delivered: isAr ? "تم التسليم" : "Delivered",
      returned: isAr ? "مرتجع" : "Returned",
    };

    return map[s] || s.replace(/_/g, " ");
  };

  const badgeClassForStatus = (value, type = "default") => {
    const s = String(value || "").trim().toLowerCase();

    if (type === "payment") {
      if (s === "paid") return "bg-success";
      if (s === "unpaid") return "bg-secondary";
      if (s === "failed" || s === "canceled" || s === "cancelled") {
        return "bg-danger";
      }
      if (s === "refunded") return "bg-info text-dark";
      return "bg-warning text-dark";
    }

    if (type === "shipping") {
      if (s === "delivered") return "bg-success";
      if (s === "shipped") return "bg-info text-dark";
      if (s === "processing" || s === "in_transit") return "bg-warning text-dark";
      if (s === "canceled" || s === "cancelled" || s === "returned") {
        return "bg-danger";
      }
      if (s === "not_required") return "bg-secondary";
      return "bg-secondary";
    }

    if (s === "completed") return "bg-success";
    if (s === "processing") return "bg-info text-dark";
    if (s === "pending") return "bg-warning text-dark";
    if (s === "canceled" || s === "cancelled") return "bg-danger";
    return "bg-secondary";
  };

  const formatDate = (value) => {
    if (!value) return labels.notAvailable;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString(isAr ? "ar" : "en", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatMoney = (value) => {
    return `${Number(value || 0).toFixed(2)} $`;
  };

  const paymentStatusRaw = String(order?.payment_status || "").toLowerCase();
  const orderStatusRaw = String(order?.status || "").toLowerCase();

  const shippingStatusRaw =
    order?.shipping_status ||
    order?.tracking_status ||
    order?.shipping_tracking_status ||
    order?.tracking?.current_status ||
    "";

  const trackingCode =
    order?.shipping_tracking_code ||
    order?.tracking_code ||
    order?.tracking?.tracking_code ||
    "";

  const trackingCarrier =
    order?.tracking_carrier ||
    order?.carrier ||
    order?.tracking?.carrier ||
    "";

  const shippedAt =
    order?.shipped_at ||
    order?.tracking?.shipped_at ||
    null;

  const deliveredAt =
    order?.delivered_at ||
    order?.tracking?.delivered_at ||
    null;

  const shippingAddress =
    order?.shipping_address ||
    order?.address ||
    "";

  const shippingMethod =
    order?.shipping_method_name ||
    order?.shipping_method?.name ||
    order?.shipping_method ||
    order?.shipping_name ||
    order?.shipping_title ||
    "";

  const shippingPrice = Number(order?.shipping_price || 0);
  const subtotal = Number(order?.subtotal || 0);
  const totalAmount = Number(order?.total || 0);
  const coinsUsed = Number(order?.coins_used || 0);
  const coinsDiscount = Number(order?.coins_discount || 0);
  const isPaid = paymentStatusRaw === "paid";
  const hasPhysicalShipping =
    shippingStatusRaw ||
    trackingCode ||
    shippingMethod ||
    shippingPrice > 0 ||
    shippingAddress;

  const paymentReference =
    order?.payment_ref ||
    order?.stripe_payment_intent ||
    order?.stripe_session_id ||
    "";

  const invoiceNumber =
    invoice?.invoice_number ||
    order?.invoice_number ||
    "";

  if (loading) {
    return (
      <div className="order-details-page" dir={dir}>
        <div className="order-details-page__frame">
          <div className="order-details-page__loading">{labels.loading}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-details-page" dir={dir}>
        <div className="order-details-page__frame">
          <div className="order-details-page__error">{error}</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-page" dir={dir}>
        <div className="order-details-page__frame">
          <div className="order-details-page__empty">{labels.notFound}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details-page" dir={dir}>
      <div className="order-details-page__frame">
        {/* Hero Section */}
        <section className="order-details-page__hero">
          <div className="order-details-page__hero-badge">{labels.details}</div>

          <div className="order-details-page__hero-header">
            <div className="order-details-page__hero-left">
              <h1>{isAr ? `الطلب #${order.id}` : `Order #${order.id}`}</h1>
              <div className="order-details-page__hero-date">
                {labels.orderDate}: {formatDate(order.created_at)}
              </div>

              <div className="order-details-page__hero-badges">
                <span className={`order-details-page__hero-badge-item ${paymentStatusRaw.toLowerCase()}`}>
                  {formatStatus(paymentStatusRaw || "pending")}
                </span>
                <span className={`order-details-page__hero-badge-item ${orderStatusRaw.toLowerCase()}`}>
                  {formatStatus(orderStatusRaw || "pending")}
                </span>
                <span className={`order-details-page__hero-badge-item ${shippingStatusRaw?.toLowerCase() || "not_required"}`}>
                  {formatStatus(shippingStatusRaw || "not_required")}
                </span>
              </div>
            </div>

            <div className="order-details-page__hero-total">
              <div className="order-details-page__hero-total-label">{labels.total}</div>
              <div className="order-details-page__hero-total-amount">{formatMoney(totalAmount)}</div>
            </div>
          </div>
        </section>

        {/* Status Cards Grid */}
        <div className="order-details-page__status-grid">
          <div className="order-details-page__status-card">
            <span className="order-details-page__status-label">{labels.paymentMethod}</span>
            <div className="order-details-page__status-value">
              {order.payment_method || labels.notAvailable}
            </div>
          </div>

          <div className="order-details-page__status-card">
            <span className="order-details-page__status-label">{labels.paymentStatus}</span>
            <div className="order-details-page__status-value">
              {formatStatus(order.payment_status)}
            </div>
          </div>

          <div className="order-details-page__status-card">
            <span className="order-details-page__status-label">{labels.orderStatus}</span>
            <div className="order-details-page__status-value">
              {formatStatus(order.status)}
            </div>
          </div>

          <div className="order-details-page__status-card">
            <span className="order-details-page__status-label">{labels.shipping}</span>
            <div className="order-details-page__status-value">
              {formatStatus(shippingStatusRaw || "not_required")}
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="order-details-page__products">
          <h3 className="order-details-page__products-title">{labels.products}</h3>

          {items.length > 0 ? (
            items.map((i, idx) => {
              const product = i.product || i.variant?.product || null;
              const name = isAr
                ? product?.name_ar || product?.name_en || `#${i.product_id}`
                : product?.name_en || product?.name_ar || `#${i.product_id}`;
              const qty = Number(i.quantity || 0);
              const price = Number(i.price || 0);
              const lineTotal = (price * qty).toFixed(2);

              return (
                <div key={i.id || idx} className="order-details-page__product-item">
                  <div className="order-details-page__product-info">
                    <div className="order-details-page__product-name">{name}</div>
                    <div className="order-details-page__product-qty">
                      {labels.qty} {qty}
                    </div>
                  </div>

                  <div className="order-details-page__product-price">
                    <div className="order-details-page__product-unit-price">{formatMoney(price)}</div>
                    <div className="order-details-page__product-line-total">{lineTotal} $</div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="order-details-page__no-items">{labels.noItems}</div>
          )}
        </div>

        {/* Summary & Payment Details */}
        <div className="order-details-page__details-grid">
          <div className="order-details-page__details-card">
            <h4 className="order-details-page__details-card-title">{labels.summary}</h4>

            <div className="order-details-page__summary-table">
              <div className="order-details-page__summary-row">
                <span className="order-details-page__summary-label">{labels.subtotal}</span>
                <span className="order-details-page__summary-value">{formatMoney(subtotal)}</span>
              </div>

              <div className="order-details-page__summary-row">
                <span className="order-details-page__summary-label">{labels.shippingPrice}</span>
                <span className="order-details-page__summary-value">{formatMoney(shippingPrice)}</span>
              </div>

              <div className="order-details-page__summary-row">
                <span className="order-details-page__summary-label">{labels.coinsUsed}</span>
                <span className="order-details-page__summary-value">{coinsUsed}</span>
              </div>

              <div className="order-details-page__summary-row">
                <span className="order-details-page__summary-label">{labels.coinsDiscount}</span>
                <span className="order-details-page__summary-value success">
                  - {formatMoney(coinsDiscount)}
                </span>
              </div>

              <div className="order-details-page__summary-row order-details-page__summary-row--total">
                <span className="order-details-page__summary-label">{labels.total}</span>
                <span className="order-details-page__summary-value">{formatMoney(totalAmount)}</span>
              </div>
            </div>
          </div>

          <div className="order-details-page__details-card">
            <h4 className="order-details-page__details-card-title">{labels.paymentDetails}</h4>

            <div className="order-details-page__detail-row">
              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.paymentMethod}</span>
                <span className="order-details-page__detail-value">
                  {order.payment_method || labels.notAvailable}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.paymentStatus}</span>
                <span className={`order-details-page__detail-value ${isPaid ? "success" : "warning"}`}>
                  {formatStatus(order.payment_status)}
                </span>
              </div>
            </div>

            <div className="order-details-page__detail-row">
              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.paymentReference}</span>
                <span className="order-details-page__detail-value">
                  {paymentReference || labels.notAvailable}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.invoiceNumber}</span>
                <span className="order-details-page__detail-value">
                  {invoiceNumber || labels.notAvailable}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tracking Details */}
        <div className="order-details-page__panel">
          <div className="order-details-page__panel-label">{labels.trackingDetails}</div>

          {hasPhysicalShipping ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.5rem" }}>
              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.shippingMethod}</span>
                <span className="order-details-page__detail-value">
                  {shippingMethod || labels.notAvailable}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.shipping}</span>
                <span className="order-details-page__detail-value">
                  {formatStatus(shippingStatusRaw || "pending")}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.trackingCode}</span>
                <span className="order-details-page__detail-value">
                  {trackingCode || labels.notAvailable}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.carrier}</span>
                <span className="order-details-page__detail-value">
                  {trackingCarrier || labels.notAvailable}
                </span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.shippedAt}</span>
                <span className="order-details-page__detail-value">{formatDate(shippedAt)}</span>
              </div>

              <div className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.deliveredAt}</span>
                <span className="order-details-page__detail-value">{formatDate(deliveredAt)}</span>
              </div>

              <div style={{ gridColumn: "1 / -1" }} className="order-details-page__detail-item">
                <span className="order-details-page__detail-label">{labels.shippingAddress}</span>
                <span className="order-details-page__detail-value" style={{ whiteSpace: "pre-wrap" }}>
                  {shippingAddress || labels.notAvailable}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--od-text-muted)" }}>
              {labels.digitalOrder} - {labels.noShippingRequired}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="order-details-page__actions">
          {invoice?.id && (
            <Link to={`/invoice/${invoice.id}`} className="order-details-page__action-btn order-details-page__action-btn--primary">
              <i className="bi bi-file-pdf" />
              {labels.viewInvoice}
            </Link>
          )}

          <Link to="/my-downloads" className="order-details-page__action-btn order-details-page__action-btn--success">
            <i className="bi bi-download" />
            {labels.downloads}
          </Link>

          <Link to="/my-orders" className="order-details-page__action-btn order-details-page__action-btn--secondary">
            <i className="bi bi-arrow-left" />
            {labels.backOrders}
          </Link>
        </div>
      </div>
    </div>
  );
}